import { ActionType } from 'typesafe-actions';
import {
  getFacebookMedia,
  getSiteMedia,
  IWixWizFacebookMedia,
  IWixWizSiteMedia,
  normalizeFacebookData,
  normalizeSiteData,
} from '../../../api/wixWiz';
import biLogger from '../../../lib/bi/biLogger';
import { MediaSource, MediaType } from '../../../types/media';
import { SUPPORTED_IMAGES_EXTENSIONS } from '../../../types/mimeTypes';
import { Thunk } from '../../rootReducer';
import { getVideoType } from '../../videoInfo/selectors';
import {
  clearFacebookMedia,
  clearSiteMedia,
  importFacebook,
  importSite,
  storeFacebookUrl,
  storeSiteUrl,
  toggleImportNotification,
} from './actions';
import { IImportedMediaItem } from './types';
import { externalUrlUploadFinish } from '@wix/bi-logger-vmaf/v2';

const PATH_EXTENSION_PATTERN = /.+(\.[a-z\d]{3,4})$/i;

function getUrlExtension(urlString) {
  const url = new URL(urlString);
  const match = url.pathname.match(PATH_EXTENSION_PATTERN);

  return match && match[1];
}

const filterByExtension = (urls, extensions) =>
  urls.filter((url) => {
    const extension = getUrlExtension(url);

    if (!extension) {
      // Can't extract extension, pass forward. Preload will determine if this a correct image
      return true;
    }

    return extensions.includes(extension);
  });

function processFacebookMedia(response: IWixWizFacebookMedia): {
  photos: IImportedMediaItem[];
  logo: string;
} {
  const normalized = normalizeFacebookData(response);

  return {
    photos: filterByExtension(
      normalized.photos,
      SUPPORTED_IMAGES_EXTENSIONS,
    ).map(makeFacebookImage),
    logo: normalized.logo,
  };
}

function importFacebookMedia(
  url: string,
): Thunk<
  ActionType<
    typeof importFacebook | typeof storeFacebookUrl | typeof clearFacebookMedia
  >,
  Promise<{ photos: IImportedMediaItem[]; logo: string }>
> {
  return async (dispatch, getState) => {
    const state = getState();
    const videoType = getVideoType(state);

    dispatch(clearFacebookMedia());

    if (!url) {
      return null;
    }

    dispatch(importFacebook.request(url));
    try {
      const facebookMediaRaw = await getFacebookMedia(url);
      const facebookMedia = processFacebookMedia(facebookMediaRaw);

      void biLogger.report(
        externalUrlUploadFinish({
          type: videoType,
          mediaCount: facebookMedia.photos.length,
          success: true,
        }),
      );

      dispatch(importFacebook.success(facebookMedia.photos));
      dispatch(storeFacebookUrl(url));

      return facebookMedia;
    } catch (error) {
      void biLogger.report(
        externalUrlUploadFinish({
          type: videoType,
          success: false,
        }),
      );

      dispatch(importFacebook.failure(error));
      throw error;
    }
  };
}

function processSiteMedia(response: IWixWizSiteMedia): IImportedMediaItem[] {
  const normalized = normalizeSiteData(response);

  return filterByExtension(normalized.photos, SUPPORTED_IMAGES_EXTENSIONS).map(
    makeSiteImage,
  );
}

function importSiteMedia(
  url: string,
): Thunk<
  ActionType<typeof importSite | typeof storeSiteUrl | typeof clearSiteMedia>,
  Promise<IImportedMediaItem[]>
> {
  return async (dispatch) => {
    dispatch(clearSiteMedia());

    if (!url) {
      return null;
    }

    dispatch(importSite.request(url));
    try {
      const siteMediaResponse = await getSiteMedia(url);
      const siteMedia = processSiteMedia(siteMediaResponse);
      dispatch(importSite.success(siteMedia));
      dispatch(storeSiteUrl(url));
      return siteMedia;
    } catch (error) {
      dispatch(importSite.failure(error));
      throw error;
    }
  };
}

function makeSiteImage(url: string): IImportedMediaItem {
  return {
    type: MediaType.IMAGE,
    source: MediaSource.SITE,
    name: url,
    url,
  };
}

function makeFacebookImage(url: string): IImportedMediaItem {
  return {
    type: MediaType.IMAGE,
    source: MediaSource.FACEBOOK,
    name: url,
    url,
  };
}

function clearImportedMedia(): Thunk<
  ActionType<typeof clearFacebookMedia | typeof clearSiteMedia>
> {
  return (dispatch) => {
    dispatch(clearFacebookMedia());
    dispatch(clearSiteMedia());
  };
}

export {
  clearImportedMedia,
  importFacebookMedia,
  importSiteMedia,
  toggleImportNotification,
};
